import * as moment from "moment-timezone";
import * as queryString from "query-string";
import * as React from "react";
import { toast } from "react-toastify";
import {
  useDispatch,
  useMappedState
} from "redux-react-hook";
import { Filter } from "react-table";

import { sentenceCase } from "src/library/utils/string-manipulations";
import {
  FilterParams,
  PaginatedParams,
  queryStringFilterData
} from "src/library/utils";
import Table, {
  ITableInstance,
  BulkSelectState
} from "src/library/components/Tables/Table";
import { Modal } from "src/library/components/Modal";
import { IAction } from "src/library/components/Button/ActionButton";
import { quickOrderFilters } from "src/core/orders/components/QuickOrderFilters";
import { BulkOrdersAdminUpdateStatusModal } from "src/core/orders/components/Modals";
import { OrderActionsList } from "src/core/orders/components/OrderActionsList";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { adminOrderTableColumns } from "src/core/shared/components/tables";
import { getOrderStatusIdFromCode } from "src/core/orders/utils/orderStatus";
import {
  BulkOrderActionsType,
  BulkUpdateOrdersParams,
  ListOrdersParams,
  ordersApi,
  OrdersResponse,
  IOrder,
  OrderNoteSourceEnum
} from "src/core/orders/api/orders.api";
import { setOrdersWithErrors } from "src/core/orders/store/orders.actions";
import { defaultPagination } from "src/core/orders/store/orders.reducer";
import type { IStore } from "src/core/shared/store";
import { OrderQuickProductAdd } from "src/core/products/components/ProductSearchComponent/OrderQuickProductAdd";
import { OrdersTableProductSelectItem } from "src/core/products/components/ProductSearchComponent/OrdersTableProductSelectItem";
import { PageProps } from "src/pages/Router/routes";
import { IProduct } from "src/core/products/api/products.api";
import { useGTMEvent } from "src/core/shared/hooks/useGTMEvent";

export const getBulkActionOptions = (
  bulkSelectData: BulkSelectState,
  setBulkActionIds: (values: number[]) => void,
  setBulkActionType: (actionType: BulkOrderActionsType | null) => void,
  selecteProduct?: IProduct | null
): IAction[] => {
  const values = Object.values(bulkSelectData || {});

  const bulkActions = [
    {
      title: "Leave a note",
      action: () => {
        // Set the bulk action IDs
        setBulkActionIds(values);
        setBulkActionType(BulkOrderActionsType.NOTE);
      }
    },
    {
      title: "Cancel",
      action: () => {
        // Set the bulk action IDs
        setBulkActionIds(values);
        setBulkActionType(BulkOrderActionsType.CANCEL);
      }
    }
  ];

  // Show the replace product option only if there is
  // a selected product.
  if (selecteProduct) {
    bulkActions.push({
      title: "Replace product",
      action: () => {
        // Set the bulk action IDs
        setBulkActionIds(values);
        setBulkActionType(BulkOrderActionsType.REPLACE_PRODUCT);
      }
    });
  }

  return bulkActions;
};

const AdminOrdersPage: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const {
    history, terminology, location
  } = props;

  const dispatch = useDispatch();
  const gtmEvent = useGTMEvent();

  const [ bulkOrdersUpdateResponse, bulkOrdersUpdate ] = ordersApi<{ rowsAffected: number; failed: number; processJobId?: number }, BulkUpdateOrdersParams>(
    "BULK_UPDATE_ORDERS"
  );

  const [ ordersResponse, getOrders ] = ordersApi<OrdersResponse, ListOrdersParams>("LIST_ORDERS");
  const ordersData = ordersResponse.data;
  const isLoading = ordersResponse.pending;
  const [ bulkActionIds, setBulkActionIds ] = React.useState<number[]>([]);
  const [ bulkActionType, setBulkActionType ] = React.useState<BulkOrderActionsType | null>(null);

  const [ showBulkActionErrorModal, setShowBulkActionErrorModal ] = React.useState<{ visable: boolean; error: string }>({
    visable: false,
    error: ""
  });

  const {
    orderStatuses, role, erp, user, installedModules
  } = useMappedState((store: IStore) => ({
    user: store.global.user,
    orderStatuses: store.global.settings ? store.global.settings.orderStatuses : [],
    role: store.global.role,
    erp: store.global.settings && store.global.settings.erp.name,
    installedModules: store.global.settings?.installedModules || []
  }));

  const [ defaultFilters, setDefaultFilters ] = React.useState<FilterParams<IOrder> | null>(null);
  const [ hasSetDefaultFilters, setHasSetDefaultFilters ] = React.useState(false);
  const [ currentSelectedProduct, setCurrentSelectedProduct ] = React.useState<IProduct | null>(null);
  const [ filterNaturalLanguage, setFilterNaturalLanguage ] = React.useState<string>("");
  const TableRef = React.useRef<ITableInstance<IOrder>>(null);

  // defaultFiltered used to populate query filter if query provided.
  const defaultFiltered = React.useMemo<Filter[] | []>(() => {
    const filterObject = queryStringFilterData(location);
    const filters = [];

    if (filterObject?.createdAt) {
      filters.push(
        {
          id: "createdAt",
          value: {
            from: moment.utc(filterObject.createdAt).startOf("day").toDate(),
            to: moment.utc(filterObject.createdAt).endOf("day").toDate()
          }
        }
      );
    }

    return filters;
  }, [ location ]);

  const reload = React.useCallback(
    (force = false, overrideFilters?: FilterParams<IOrder>) => {
      if (TableRef.current) {
        TableRef.current.reloadTable(force, overrideFilters);
      }
    },
    [ TableRef ]
  );

  React.useEffect(() => {
    reload();
  }, [ currentSelectedProduct, reload ]);

  // Get the context filters from the URL query parameters.
  React.useEffect(() => {
    const filterObject: FilterParams<IOrder> & {
      deliveryDate?: string;
      error?: string;
    } = queryStringFilterData(location) || {};

    const query = queryString.parse(location.search);
    const naturallanguagePrefix = `Showing ${terminology.order.plural.text} `;
    const naturalLanguagePieces: string[] = [];
    let naturalLanguage = "";

    if (query.filters) {
      if (filterObject.error) {
        if (filterObject.error === "all") {
          filterObject.orderStatusId = getOrderStatusIdFromCode(orderStatuses, "error");
          naturalLanguagePieces.push("with errors");
        }

        delete filterObject.error;
      }

      if (filterObject.deliveryDate) {
        filterObject.orderDeliveryDate = {
          $between: [
            moment
              .utc(filterObject.deliveryDate, "YYYY-MM-DD")
              .startOf("day")
              .toDate(),
            moment
              .utc(filterObject.deliveryDate, "YYYY-MM-DD")
              .endOf("day")
              .toDate()
          ]
        };
        naturalLanguagePieces.push(`to be delivered on ${moment.utc(filterObject.deliveryDate, "YYYY-MM-DD").format("DD MMM YYYY")}`);
        delete filterObject.deliveryDate;
      }

      if (filterObject.createdAt) {
        naturalLanguagePieces.push(`created on ${moment.utc(filterObject.createdAt, "YYYY-MM-DD").format("DD MMM YYYY")}`);

        filterObject.createdAt = {
          $between: [
            moment
              .utc(filterObject.createdAt, "YYYY-MM-DD")
              .startOf("day")
              .toDate(),
            moment
              .utc(filterObject.createdAt, "YYYY-MM-DD")
              .endOf("day")
              .toDate()
          ]
        };
      }
    }

    if (currentSelectedProduct) {
      naturalLanguagePieces.push(`containing ${terminology?.partNumber.singular.text || "part number"}: ${currentSelectedProduct.partNumber} - ${currentSelectedProduct.description}`);
    }

    // Set context natural language explanation.
    if (naturalLanguagePieces.length > 0) {
      naturalLanguage = `${naturallanguagePrefix} ` + naturalLanguagePieces.join(", ");
    }

    setFilterNaturalLanguage(naturalLanguage);
    setDefaultFilters(filterObject);
    setHasSetDefaultFilters(true);
  }, [
    currentSelectedProduct,
    terminology.order.plural.text,
    terminology?.partNumber.singular.text,
    orderStatuses,
    location
  ]);

  // Clear query parameters and, therefore, query parameters.
  const clearDefaultFilters = React.useCallback(() => {
    setFilterNaturalLanguage("");
    setDefaultFilters(null);
    setCurrentSelectedProduct(null);
  }, []);

  const onFetchData = React.useCallback((allFilters: FilterParams<IOrder>, pagination: PaginatedParams<IOrder>) => {
    if (hasSetDefaultFilters) {
      const { orderStatus, ...filters } = allFilters;

      const params = {
        pagination,
        filters: {
          ...filters,
          orderStatusId: orderStatus ? parseInt(orderStatus.id, 10) : undefined,
          productId: currentSelectedProduct?.id
        }
      };

      getOrders<ListOrdersParams>(params);
    }
  }, [
    currentSelectedProduct,
    getOrders,
    hasSetDefaultFilters
  ]);

  // On bulkOrdersUpdateResponse
  React.useEffect(() => {
    if (bulkOrdersUpdateResponse.error) {
      // Show the error returned
      setShowBulkActionErrorModal({
        visable: true,
        error: bulkOrdersUpdateResponse.error.message
      });
      // Shut the bulk action update status modal
      setBulkActionType(null);
    } else if (bulkOrdersUpdateResponse.data) {
      if (bulkActionType === BulkOrderActionsType.REPLACE_PRODUCT) {
        history.push(`/admin/queue/${bulkOrdersUpdateResponse.data.processJobId}`);
      } else if (bulkOrdersUpdateResponse.data.failed && bulkOrdersUpdateResponse.data.rowsAffected) {
        setShowBulkActionErrorModal({
          visable: true,
          error: `<p>Some ${props.terminology.order.plural.text} could not be updated by this action: <ul><li>${
            bulkOrdersUpdateResponse.data.failed
          } ${props.terminology.order.plural.text} failed</li> <li>${bulkOrdersUpdateResponse.data.rowsAffected} ${
            props.terminology.order.plural.text
          } successfully updated</li></ul></p>`
        });
      } else if (bulkOrdersUpdateResponse.data.failed) {
        setShowBulkActionErrorModal({
          visable: true,
          error: `None of the ${props.terminology.order.plural.text} selected could be updated.`
        });
      } else if (bulkOrdersUpdateResponse.data.rowsAffected) {
        toast.success(`${bulkOrdersUpdateResponse.data.rowsAffected} ${props.terminology.order.plural.text} have been updated successfully.`);
      }

      // Shut the bulk action update status modal
      setBulkActionType(null);
      reload();
    }
  }, [ bulkOrdersUpdateResponse ]);

  React.useEffect(() => {
    if (ordersResponse.data) {
      dispatch(setOrdersWithErrors(ordersResponse.data.allOrdersWithErrors));
    }
  }, [ ordersResponse ]);

  const memoGetBulkActionOptions = React.useCallback(
    (bulkSelectData: BulkSelectState) => getBulkActionOptions(bulkSelectData, setBulkActionIds, setBulkActionType, currentSelectedProduct),
    [
      erp,
      terminology,
      setBulkActionIds,
      setBulkActionType,
      currentSelectedProduct
    ]
  );

  const columns = React.useMemo(() => adminOrderTableColumns({
    installedModules,
    ActionButton: row => {
      if (erp && role) {
        return (
          <OrderActionsList
            erp={erp}
            terminology={terminology}
            role={role}
            order={row.original}
            reload={reload}
          />
        );
      }

      return null;
    },
    terminology,
    orderStatuses
  }), [
    erp,
    installedModules,
    orderStatuses,
    reload,
    role,
    terminology
  ]);

  return (
    <Layout {...props}>
      <MainContentWrapper fill>
        <Table
          tableId="global"
          ref={TableRef}
          tableOf={terminology.order.plural.text}
          pages={ordersData ? ordersData.orders.pages : 0}
          totalRows={ordersData ? ordersData.orders.count : 0}
          loading={isLoading}
          // If there are default filters applied, don't display the quick filters.
          disabledQuickFilters={defaultFilters !== null && Object.keys(defaultFilters).length ? "Quick filters cannot be used when other filters are already applied" : undefined}
          clearFilters={clearDefaultFilters}
          quickFilters={quickOrderFilters({
            terminology,
            orderStatuses: orderStatuses || []
          })}
          defaultPagination={defaultPagination}
          data={ordersData ? ordersData.orders.result : []}
          onFetchData={onFetchData}
          error={ordersResponse.error}
          mainAction={row => history.push(`/orders/${row.original.id}`)}
          defaultFilters={defaultFilters ? defaultFilters : undefined}
          defaultFiltered={defaultFiltered}
          filterNaturalLanguage={filterNaturalLanguage}
          additionalFilters={(
            <OrderQuickProductAdd
              showProductsToggle={false}
              ProductAddItemComponent={OrdersTableProductSelectItem}
              backToProductsButton={false}
              label={sentenceCase("Search orders containing a product")}
              onProductSelect={product => {
                if (product) {
                  setCurrentSelectedProduct(product);
                }
              }}
            />
          )}
          bulkActionOptions={bulkSelectData => memoGetBulkActionOptions(bulkSelectData)}
          columns={columns}
        />

        <BulkOrdersAdminUpdateStatusModal
          productReplaceFrom={currentSelectedProduct}
          terminology={props.terminology}
          show={bulkActionType !== null}
          loading={bulkOrdersUpdateResponse.pending}
          type={bulkActionType}
          bulkActionIds={bulkActionIds}
          onConfirm={({
            orderNote, fromProductId, toProductId
          }) => {
            const updateData: BulkUpdateOrdersParams = {
              ids: bulkActionIds,
              type: bulkActionType as BulkOrderActionsType,
              data: {}
            };

            if (orderNote && orderNote.length) {
              updateData.data.note = {
                body: orderNote,
                authorId: user ? user.id : 0,
                source: OrderNoteSourceEnum.STAFF
              };
            }

            // product replacement
            if (fromProductId && toProductId) {
              updateData.data.fromProductId = fromProductId;
              updateData.data.toProductId = toProductId;
            }

            // Update the selected the orders
            bulkOrdersUpdate(updateData);
          }}
          handleDismiss={() => {
            // Only allow the user to backout of the modal only if the request
            // is not pending
            if (!bulkOrdersUpdateResponse.pending) {
              setBulkActionType(null);
            }
          }}
        />

        <Modal
          small
          type="notice"
          show={showBulkActionErrorModal.visable}
          title="Bulk Action Error"
          body={showBulkActionErrorModal.error}
          actions={[
            {
              name: "OK",
              action: () => setShowBulkActionErrorModal({
                visable: false,
                error: ""
              }),
              $buttonType: "primary"
            }
          ]}
          backgroundAction={() => setShowBulkActionErrorModal({
            visable: false,
            error: ""
          })}
        />
      </MainContentWrapper>
    </Layout>
  );
};

export default AdminOrdersPage;
